import React, {useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {IPoi} from "../../maps/types/IPoi";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {TransportPoiType} from "../../maps/utils/PoiType";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ViewType} from "../../view_type/ViewType";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";

interface IProps {
    className?: string;
    offer: IPropertyDetailOffer;
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP, source?: GTMEventApplicationSourceSection) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const offer = {...props.offer, geo_area: {coordinates: {coordinates: props.geoAreaCoordinates}}};

    const hasCountryPois = getHasCountryPois(props.offer.region.country);
    const {data: poiData} = useGetOfferDetailTransportPoiQuery(offer?.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });
    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = (click?: {isFullscreenClick: boolean}) => {
        props.changeModalTab(
            OfferModalActiveTab.MAP,
            click?.isFullscreenClick ? GTMEventApplicationSourceSection.ZOOM_MAP : GTMEventApplicationSourceSection.BUTTON_MAP
        );
    };

    return (
        <>
            <div className={wrapperStyle}>
                <Text variant="headline_4" as="h2" className={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} className={mapWrapperStyle} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            className={mapCustomStyles}
                            onFullscreenClick={() => openMapModal({isFullscreenClick: true})}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div className={contentWrapperStyle}>
                        <OfferDetailLocationSummary {...props} routes={routes} />

                        <Button
                            variant="outlined_secondary"
                            className={css({width: "100%", mt: 3})}
                            onClick={() => openMapModal({isFullscreenClick: false})}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.CHECK_ON_MAP_BUTTON}
                        >
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            {offer.region.country === Country.POLAND && (
                <Accordion dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.MORE_ABOUT_LOCATION}>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Przeczytaj więcej o lokalizacji
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        {props.offer.location_description && (
                            <Text className={cx(css({mt: 0}), preLine)} mb={2} variant="body_copy_1">
                                {props.offer.location_description}
                            </Text>
                        )}

                        {props.offer.region.included_location?.description && (
                            <Text className={preLine} variant="body_copy_1">
                                {props.offer.region.included_location.description}
                            </Text>
                        )}
                    </Accordion.Content>
                </Accordion>
            )}
        </>
    );
};

const wrapperStyle = css({
    background: "#fff",
    borderRadius: 2,
    py: 2,
    mb: 2,

    md: {
        py: 3
    }
});

const headerStyle = css({
    px: 1.5,
    mb: 2,

    md: {
        px: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    height: "52rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    md: {
        height: "41rem",
        mx: 3
    }
});

const contentWrapperStyle = css({
    px: 1.5,

    md: {
        px: 3
    }
});

const preLine = css({
    whiteSpace: "pre-line"
});

const mapCustomStyles = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});
import React, {useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {IPoi} from "../../maps/types/IPoi";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {TransportPoiType} from "../../maps/utils/PoiType";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ViewType} from "../../view_type/ViewType";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";

interface IProps {
    className?: string;
    offer: IPropertyDetailOffer;
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP, source?: GTMEventApplicationSourceSection) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const offer = {...props.offer, geo_area: {coordinates: {coordinates: props.geoAreaCoordinates}}};

    const hasCountryPois = getHasCountryPois(props.offer.region.country);
    const {data: poiData} = useGetOfferDetailTransportPoiQuery(offer?.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });
    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = (click?: {isFullscreenClick: boolean}) => {
        props.changeModalTab(
            OfferModalActiveTab.MAP,
            click?.isFullscreenClick ? GTMEventApplicationSourceSection.ZOOM_MAP : GTMEventApplicationSourceSection.BUTTON_MAP
        );
    };

    return (
        <>
            <div className={wrapperStyle}>
                <Text variant="headline_4" as="h2" className={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} className={mapWrapperStyle} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            className={mapCustomStyles}
                            onFullscreenClick={() => openMapModal({isFullscreenClick: true})}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div className={contentWrapperStyle}>
                        <OfferDetailLocationSummary {...props} routes={routes} />

                        <Button
                            variant="outlined_secondary"
                            className={css({width: "100%", mt: 3})}
                            onClick={() => openMapModal({isFullscreenClick: false})}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.CHECK_ON_MAP_BUTTON}
                        >
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            {offer.region.country === Country.POLAND && (
                <Accordion dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.MORE_ABOUT_LOCATION}>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Przeczytaj więcej o lokalizacji
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        {props.offer.location_description && (
                            <Text className={cx(css({mt: 0}), preLine)} mb={2} variant="body_copy_1">
                                {props.offer.location_description}
                            </Text>
                        )}

                        {props.offer.region.included_location?.description && (
                            <Text className={preLine} variant="body_copy_1">
                                {props.offer.region.included_location.description}
                            </Text>
                        )}
                    </Accordion.Content>
                </Accordion>
            )}
        </>
    );
};

const wrapperStyle = css({
    background: "#fff",
    borderRadius: 2,
    py: 2,
    mb: 2,

    md: {
        py: 3
    }
});

const headerStyle = css({
    px: 1.5,
    mb: 2,

    md: {
        px: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    height: "52rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    md: {
        height: "41rem",
        mx: 3
    }
});

const contentWrapperStyle = css({
    px: 1.5,

    md: {
        px: 3
    }
});

const preLine = css({
    whiteSpace: "pre-line"
});

const mapCustomStyles = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});
